import React from "react";
import { observer } from "mobx-react";

import { useParams } from "react-router-dom";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { Box, IconButton } from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";

import { humanFileSize } from "utils/formatters/file";
import { formatMomentDate } from "utils/formatters/timezone";

import API from "utils/api";

import { useStore } from "utils/hooks/store";

import { colors } from "theme/colors";

import {
  FileContainer as FileContainerStyled,
  FileName,
  FileData,
  FileIconContainer,
  Wrapper,
  FileContainerUploading,
  FileContainerUploadingError,
  ProgressBar,
  ProgressBarFill,
  ProgressText,
} from "./styles";

interface AttachmentNodeProps {
  node: {
    attrs: {
      name?: string;
      href?: string;
      size?: number;
      mime?: string;
      uploading?: boolean;
      uploadId?: string;
      error?: boolean;
      errorMessage?: string | null;
      progress?: number;
      creator?: { name: string; uuid: string };
      created?: string;
      file?: string;
      filename?: string;
      thumbnail?: string;
    };
  };
}

const FileContainer: React.FC<AttachmentNodeProps> = ({ node }) => {
  const { attrs } = node;
  const { shareId } = useParams<{ shareId?: string }>();
  const { notification } = useStore();

  const attachment = {
    uuid: attrs.uploadId || "",
    type: "file" as const,
    creator: attrs.creator,
    created: attrs.created || "",
    file: attrs.file || attrs.href || "",
    filename: attrs.filename || attrs.name || "",
    thumbnail: attrs.thumbnail,
    size: attrs.size || 0,
  };

  if (attrs.uploading) {
    const progress = attrs.progress || 0;
    return (
      <FileContainerUploading key={attachment.uuid}>
        <Wrapper style={{ flex: 1 }}>
          <FileIconContainer>
            <UploadFileIcon sx={{ color: "#757575" }} />
          </FileIconContainer>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FileName>{attachment.filename}</FileName>
              <ProgressText>{progress}%</ProgressText>
            </Box>
            <ProgressBar>
              <ProgressBarFill $progress={progress} />
            </ProgressBar>
          </Box>
        </Wrapper>
      </FileContainerUploading>
    );
  }

  if (attrs.error) {
    return (
      <FileContainerUploadingError key={attachment.uuid}>
        <Wrapper>
          <FileIconContainer>
            <UploadFileIcon sx={{ color: colors.error.main }} />
          </FileIconContainer>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <FileName>{attachment.filename}</FileName>
            <FileData style={{ color: colors.error.main }}>{attrs.errorMessage ?? "Upload failed"}</FileData>
          </Box>
        </Wrapper>
        <Wrapper>
          <Box sx={{ width: "40px", height: "40px" }} />
        </Wrapper>
      </FileContainerUploadingError>
    );
  }

  return (
    <FileContainerStyled key={attachment.uuid}>
      <Wrapper>
        <FileIconContainer>
          <UploadFileIcon sx={{ color: "#757575" }} />
        </FileIconContainer>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <FileName>{attachment.filename}</FileName>
          <FileData>
            {humanFileSize(attachment.size)} | Uploaded by {attachment.creator?.name} on{" "}
            <span data-testid="document-attachment-created-date">
              {formatMomentDate(attachment.created, "D MMM, YYYY")}
            </span>
          </FileData>
        </Box>
      </Wrapper>
      <Wrapper>
        <IconButton
          sx={{
            borderRadius: "50px",
            background: colors.action.hover,
            "&:hover": {
              background: colors.action.selected,
            },
          }}
          onClick={() =>
            Promise.resolve(API.downloadFile(attachment.file, shareId)()).catch(() =>
              notification.enqueueErrorSnackbar("Could not download file. Please try again later."),
            )
          }
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
      </Wrapper>
    </FileContainerStyled>
  );
};

export default observer(FileContainer);
